import { useState } from 'react';
import { Code, Copy, Check } from 'lucide-react';

export default function CodeSnippetGenerator() {
  const [apiKey, setApiKey] = useState('your_api_key_here');
  const [endpoint, setEndpoint] = useState('/api/convert/typescript');
  const [language, setLanguage] = useState<'javascript' | 'python' | 'curl' | 'typescript'>('javascript');
  const [copied, setCopied] = useState(false);

  const endpoints = [
    { value: '/api/convert/typescript', label: 'TypeScript' },
    { value: '/api/convert/zod', label: 'Zod' },
    { value: '/api/convert/openapi', label: 'OpenAPI' },
    { value: '/api/convert/toon', label: 'JSON → TOON' },
    { value: '/api/convert/from-toon', label: 'TOON → JSON' },
    { value: '/api/convert/all', label: 'All Conversions' },
  ];

  const generateSnippet = () => {
    const baseUrl = window.location.origin;

    switch (language) {
      case 'javascript':
        return `// Using Fetch API
const response = await fetch('${baseUrl}${endpoint}', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': '${apiKey}'
  },
  body: JSON.stringify({
    json: { /* your JSON data here */ }
  })
});

const data = await response.json();
console.log(data);`;

      case 'typescript':
        return `// Using Fetch API with TypeScript
interface ConversionRequest {
  json: Record<string, any>;
}

interface ConversionResponse {
  success: boolean;
  result?: any;
  error?: string;
}

const convertData = async (jsonData: Record<string, any>): Promise<ConversionResponse> => {
  const response = await fetch('${baseUrl}${endpoint}', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': '${apiKey}'
    },
    body: JSON.stringify({ json: jsonData })
  });

  return await response.json();
};

// Usage
const result = await convertData({ name: "John", age: 30 });
console.log(result);`;

      case 'python':
        return `# Using requests library
import requests
import json

url = '${baseUrl}${endpoint}'
headers = {
    'Content-Type': 'application/json',
    'X-API-Key': '${apiKey}'
}
data = {
    'json': {
        # your JSON data here
    }
}

response = requests.post(url, headers=headers, json=data)
result = response.json()
print(result)`;

      case 'curl':
        return `# Using cURL
curl -X POST '${baseUrl}${endpoint}' \\
  -H 'Content-Type: application/json' \\
  -H 'X-API-Key: ${apiKey}' \\
  -d '{
    "json": {
      "name": "John",
      "age": 30
    }
  }'`;

      default:
        return '';
    }
  };

  const snippet = generateSnippet();

  const copySnippet = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const languages = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'python', label: 'Python' },
    { value: 'curl', label: 'cURL' },
  ];

  return (
    <div className="bg-white dark:bg-dark-surface rounded-xl p-6 border border-gray-200 dark:border-dark-border">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-1 flex items-center gap-2">
          <Code className="w-5 h-5" />
          Code Snippet Generator
        </h3>
        <p className="text-sm text-gray-500 dark:text-dark-muted">
          Generate ready-to-use code for your favorite language
        </p>
      </div>

      {/* Configuration */}
      <div className="space-y-3 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-2">
            API Key
          </label>
          <input
            type="text"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="your_api_key_here"
            className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg font-mono text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-2">
              Language
            </label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-500"
            >
              {languages.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-2">
              Endpoint
            </label>
            <select
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-500"
            >
              {endpoints.map((ep) => (
                <option key={ep.value} value={ep.value}>
                  {ep.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Generated snippet */}
      <div className="relative">
        <pre className="bg-gray-900 dark:bg-black text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono max-h-96">
          {snippet}
        </pre>
        <button
          onClick={copySnippet}
          className="absolute top-3 right-3 p-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
          title="Copy to clipboard"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <Copy className="w-4 h-4 text-gray-400" />
          )}
        </button>
      </div>

      {/* Quick tips */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-xs text-blue-900 dark:text-blue-100">
          <strong>Tip:</strong> Replace <code className="px-1 py-0.5 bg-blue-100 dark:bg-blue-900/40 rounded">your_api_key_here</code> with your actual API key from the Dashboard.
        </p>
      </div>
    </div>
  );
}
